import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, TrendingUp, TrendingDown, Tag, Sparkles, Lightbulb, ArrowRight, Loader2, GraduationCap, Clock } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";

const ICONS = {
  "alert-triangle": AlertTriangle,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  "tag": Tag,
  "sparkles": Sparkles,
};

const TYPE_STYLES = {
  stop_loss: { bg: "bg-[#FCEAE8]", border: "border-[#F2C4BE]", icon: "text-[#8E3B2A]", title: "text-[#8E3B2A]" },
  loss_margin: { bg: "bg-[#FCEAE8]", border: "border-[#F2C4BE]", icon: "text-[#8E3B2A]", title: "text-[#8E3B2A]" },
  restock: { bg: "bg-[#FDF6E3]", border: "border-[#F2DEB0]", icon: "text-[#8B6518]", title: "text-[#8B6518]" },
  price_setup: { bg: "bg-[#FDF6E3]", border: "border-[#F2DEB0]", icon: "text-[#8B6518]", title: "text-[#8B6518]" },
  promote: { bg: "bg-[#E8F3EC]", border: "border-[#C5E1D1]", icon: "text-[#2B5A3F]", title: "text-[#2B5A3F]" },
};

function LearningsBanner({ stats }) {
  if (!stats || (!stats.total && !stats.ready)) return null;
  const { total, ready, time_saved_seconds_per_scan } = stats;
  const inProgress = Math.max(0, total - ready);

  return (
    <div
      data-testid="learnings-banner"
      className="mag-card p-4 mb-3 bg-gradient-to-r from-[#E8F3EC] to-[#F4F1ED] border-[#C5E1D1] flex items-center gap-3 flex-wrap"
    >
      <div className="h-10 w-10 rounded-xl bg-[#2B5A3F] text-white flex items-center justify-center flex-shrink-0">
        <GraduationCap size={20} />
      </div>
      <div className="flex-1 min-w-[180px]">
        <h4 className="text-sm font-semibold text-[#2B5A3F] leading-tight">
          {ready > 0
            ? `Ho imparato ${ready} ${ready === 1 ? "preferenza" : "preferenze"} di scadenza`
            : `${total} ${total === 1 ? "preferenza" : "preferenze"} in apprendimento`}
        </h4>
        <p className="text-xs text-[#5C5F5A] mt-0.5 leading-relaxed">
          {ready > 0 ? (
            <>
              Le prossime scansioni le applicano in automatico
              {time_saved_seconds_per_scan > 0 ? (
                <>
                  {" "}— risparmi <span className="font-mono font-semibold">~{time_saved_seconds_per_scan}s</span> per scan
                </>
              ) : null}
              {inProgress > 0 ? (
                <>
                  {" "}· altre <span className="font-mono">{inProgress}</span> in apprendimento
                </>
              ) : null}
              .
            </>
          ) : (
            <>Servono almeno 3 conferme per attivare l&apos;apprendimento automatico.</>
          )}
        </p>
      </div>
      {ready > 0 && (
        <div data-testid="learnings-stat-pill" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-[#C5E1D1] text-[#2B5A3F]">
          <Clock size={12} />
          <span className="text-xs font-mono font-semibold">~{time_saved_seconds_per_scan}s</span>
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const [items, setItems] = useState(null);
  const [learningsStats, setLearningsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dashboard/insights`);
        if (active) {
          setItems(data.insights || []);
          setLearningsStats(data.learnings_stats || null);
        }
      } catch (_e) {
        if (active) {
          setItems([]);
          setLearningsStats(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div data-testid="insights-loading" className="mag-card p-6 flex items-center gap-3 text-sm text-[#8E918C]">
        <Loader2 size={16} className="animate-spin" /> Analizzo il magazzino...
      </div>
    );
  }

  // Show learnings banner even when there are no urgent insights
  if (!items || items.length === 0) {
    return (
      <section data-testid="insights-section">
        <LearningsBanner stats={learningsStats} />
        <div data-testid="insights-empty" className="mag-card p-6 text-center">
          <Sparkles className="mx-auto text-[#2B5A3F] mb-2" size={24} />
          <h3 className="text-sm font-semibold mb-1">Tutto sotto controllo</h3>
          <p className="text-xs text-[#8E918C]">Nessuna azione urgente da segnalare. Continua così!</p>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="insights-section">
      <LearningsBanner stats={learningsStats} />
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={16} className="text-[#8B6518]" />
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C]">
          Cosa spingere oggi
        </p>
        <span data-testid="insights-count" className="text-[10px] font-mono text-[#5C5F5A] bg-[#F4F1ED] border border-[#EAE7E1] h-5 px-1.5 rounded inline-flex items-center">
          {items.length}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((it, idx) => {
          const Icon = ICONS[it.icon] || Sparkles;
          const style = TYPE_STYLES[it.type] || TYPE_STYLES.promote;
          return (
            <button
              key={idx}
              data-testid={`insight-${it.type}-${idx}`}
              onClick={() => navigate(it.product_id ? `/app/prodotti?open=${it.product_id}` : "/app/prodotti")}
              className={`text-left mag-card p-4 ${style.bg} ${style.border} hover:shadow-sm transition-all group`}
            >
              <div className="flex items-start gap-3">
                <Icon size={18} className={`${style.icon} flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold leading-tight ${style.title}`}>{it.title}</h4>
                  <p className="text-xs text-[#5C5F5A] mt-1 leading-relaxed">{it.message}</p>
                  <div className={`text-xs font-medium mt-2 inline-flex items-center gap-1 ${style.title} group-hover:gap-1.5 transition-all`}>
                    {it.action_label || "Vai"}
                    <ArrowRight size={12} />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
